import {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useWebsiteChatbot } from './use-website-chatbot';
import { BOT_NAME } from './constants';
import { MessageRenderer } from './message-renderer';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { X, ArrowUp } from 'lucide-react';
import { trackChatbotEvent } from './analytics';

const MOBILE_CHAT_LAYOUT_MQ = '(max-width: 767px)';

function useIsMobileChatLayout() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(MOBILE_CHAT_LAYOUT_MQ).matches
      : false
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_CHAT_LAYOUT_MQ);
    const syncLayout = () => setIsMobile(mediaQuery.matches);
    syncLayout();
    mediaQuery.addEventListener('change', syncLayout);
    return () => mediaQuery.removeEventListener('change', syncLayout);
  }, []);

  return isMobile;
}

function ChatMessagesViewport({
  children,
  scrollKey,
}: {
  children: ReactNode;
  scrollKey: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollToBottom = () => {
      const el = containerRef.current;
      if (!el) return;
      // Only scroll when the content actually overflows, and scroll the
      // container only — never the page/window.
      if (el.scrollHeight <= el.clientHeight) return;
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    };
    scrollToBottom();
    const timeoutId = window.setTimeout(scrollToBottom, 120);
    const lateTimeoutId = window.setTimeout(scrollToBottom, 320);
    return () => {
      window.clearTimeout(timeoutId);
      window.clearTimeout(lateTimeoutId);
    };
  }, [scrollKey]);

  return (
    <div
      ref={containerRef}
      className="min-h-0 flex-1 overflow-y-auto bg-white px-4 dark:bg-[#1f1f1f]"
    >
      <div className="flex flex-col gap-3 py-2 pb-4">{children}</div>
    </div>
  );
}

function ChatPanelContent() {
  const {
    open,
    loading,
    session,
    error,
    handleOpen,
    handleClose,
    sendQuickAction,
    sendText,
  } = useWebsiteChatbot();
  const navigate = useNavigate();
  const isMobileLayout = useIsMobileChatLayout();
  const [input, setInput] = useState('');

  const handleBookCleaning = useCallback(() => {
    trackChatbotEvent('chatbot_booking_started', {
      chatbot_session_id: session?.id,
    });
    void handleClose();
    navigate({ to: '/booking' });
  }, [session?.id, handleClose, navigate]);

  const handleQuickAction = useCallback(
    (id: string) => {
      if (id === 'book_cleaning') {
        handleBookCleaning();
        return;
      }
      void sendQuickAction(id);
    },
    [handleBookCleaning, sendQuickAction]
  );

  const handleSend = async () => {
    const value = input.trim();
    if (!value) return;
    setInput('');
    await sendText(value);
  };

  const hasActiveFlowCard = session?.messages.some(message =>
    ['escalation_form'].includes(message.message_type)
  );

  const showChatFooter = Boolean(session && !hasActiveFlowCard);

  const messageScrollKey = [
    session?.messages.length ?? 0,
    loading ? 'loading' : 'idle',
    hasActiveFlowCard ? 'flow' : 'chat',
    error ?? '',
  ].join('-');

  const showWelcomeHero =
    !hasActiveFlowCard && (session?.messages.length ?? 0) === 0;

  const messageList = (
    <>
      {showWelcomeHero && (
        <div className="flex flex-col items-center gap-5 pb-6 pt-10 text-center">
          <div className="relative flex h-24 w-24 items-center justify-center">
            <span className="absolute inset-0 animate-pulse rounded-full bg-[#FF6A00] opacity-40 blur-2xl" />
            <span className="relative h-20 w-20 rounded-full border-[7px] border-[#FF6A00] bg-white shadow-[0_0_40px_rgba(255,106,0,0.55)] dark:bg-[#1f1f1f]" />
          </div>
          <h2 className="text-lg font-semibold">Hi, I&apos;m {BOT_NAME}</h2>
        </div>
      )}
      {session?.messages.map(message => (
        <MessageRenderer
          key={message.id}
          message={message}
          sessionId={session!.id}
          onQuickAction={handleQuickAction}
          onNavigateBooking={handleBookCleaning}
        />
      ))}
      {loading && (
        <p className="text-xs text-muted-foreground">
          Itria Assistant is typing…
        </p>
      )}
      {error && <p className="text-sm text-red-500">{error}</p>}
    </>
  );

  const panelBody = (
    <>
      <ChatMessagesViewport scrollKey={messageScrollKey}>
        {messageList}
      </ChatMessagesViewport>

      {session && (
        <div className="shrink-0 bg-white px-4 py-4 dark:bg-[#1f1f1f]">
          {showChatFooter ? (
            <>
              <div className="mb-3 flex flex-wrap justify-center gap-2">
                {session.quick_actions.map(action => (
                  <button
                    key={action.id}
                    type="button"
                    className="rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 dark:hover:bg-white/10"
                    onClick={() => handleQuickAction(action.id)}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 py-1.5 pl-4 pr-1.5 dark:border-white/10 dark:bg-white/5">
                <input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder={`Ask ${BOT_NAME}…`}
                  onKeyDown={e => {
                    if (e.key === 'Enter') void handleSend();
                  }}
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
                />
                <button
                  type="button"
                  aria-label="Send message"
                  className="grid h-9 w-9 place-items-center rounded-full bg-[#FF6A00] text-white transition-colors hover:bg-[#e55f00] disabled:opacity-50"
                  onClick={() => void handleSend()}
                  disabled={loading || !input.trim()}
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
              </div>
            </>
          ) : (
            <p className="text-center text-xs text-muted-foreground">
              Complete the step above to continue.
            </p>
          )}
        </div>
      )}
    </>
  );

  return (
    <>
      {!open && (
        <button
          type="button"
          aria-label={`Ask ${BOT_NAME}`}
          onClick={() => void handleOpen()}
          className="fixed bottom-5 right-5 z-[60] rounded-full transition-transform hover:scale-105 md:bottom-6 md:right-6"
        >
          <img
            src="/chat-bot.png"
            alt={`Ask ${BOT_NAME}`}
            className="h-12 w-auto md:h-14"
          />
        </button>
      )}

      {/* Desktop panel — do not keep Vaul Drawer open here; outside clicks dismiss it */}
      {open && !isMobileLayout && (
        <div className="chatbox-animated-border fixed bottom-6 right-6 z-[60] h-[560px] w-[380px] shadow-2xl">
          <div className="flex h-full w-full flex-col overflow-hidden rounded-[calc(1.5rem-2.5px)] bg-white dark:bg-[#1f1f1f]">
            <div className="flex items-center justify-between px-4 py-3">
              <p className="font-semibold">{BOT_NAME}</p>
              <button
                type="button"
                aria-label="Close chat"
                onClick={() => void handleClose()}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {panelBody}
            </div>
          </div>
        </div>
      )}

      {/* Mobile drawer — mount only on small screens so desktop clicks are not "outside" */}
      {isMobileLayout && (
        <Drawer
          open={open}
          onOpenChange={value => !value && void handleClose()}
        >
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>{BOT_NAME}</DrawerTitle>
            </DrawerHeader>
            <div className="flex max-h-[min(640px,85vh)] flex-col px-2 pb-6">
              {panelBody}
            </div>
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
}

export function WebsiteChatbot() {
  return (
    <Suspense fallback={null}>
      <ChatPanelContent />
    </Suspense>
  );
}

export default WebsiteChatbot;
